/**
 * Media Carousel — the ONE component that replaces the dead YouTube
 * search-playlist iframe on all four surfaces that used to carry it
 * (`screens/Detail.tsx`, `desktop/screens/Detail.tsx`, `sheets/WhatsNewSheet.tsx`,
 * `desktop/modals/WhatsNewModal.tsx`). See PORTING-NOTES P10-01.
 *
 * Everything derived comes from `selectors.mediaVM` — the ordering (crew videos
 * → Steam trailers → screenshots), `canDel`, `canAdd`/`addTip` and `ytLink` are
 * all decided there, so this file only knows how to *paint* a `MediaItem` and
 * which one is on stage. The four local pieces of state are genuinely local:
 * which tile is active, which trailers 404'd, and the two composer fields.
 *
 * WHAT IS STILL AN IFRAME. The crew half of the carousel is a per-video
 * `youtube-nocookie.com/embed/<key>` frame — still an `<iframe>`, never a
 * `<script>`, so "zero runtime third-party JS" holds — and it carries
 * `referrerPolicy="origin"` because PORTING-NOTES P3c-03 binds every embed
 * surface to it (referrer-less embeds render YouTube Error 153). Only the ACTIVE
 * item is mounted; a game with nine crew clips must not open nine players.
 *
 * The Steam half is `<video>` over Steam's own `hls_h264` playlist and `<img>`
 * over the screenshot. HLS is not a progressive file: Safari plays it natively,
 * everything else needs Media Source Extensions, which is what `hls.js` is for
 * (PORTING-NOTES P10-04 — it is the fifth runtime package, and the reason the
 * pin in docs/PLAN.md §1 moved).
 *
 * A trailer only leaves the set when the TRAILER is what failed — an unrecovered
 * FATAL `hls.js` error, or a native `error` event on Safari. Nothing about the
 * environment prunes. That distinction is the bug fix: the first cut pruned on
 * the bare `error` event of a `<video>` pointed at a guessed `movie480.mp4` URL,
 * and since the element carried `preload="none"` the guess was not tested until
 * the user pressed play — so every trailer Steam had re-encoded deleted itself,
 * and the `N/M` counter with it, at the exact moment somebody tried to watch it.
 *
 * The panel heading lives OUTSIDE this component — each surface renders its own,
 * because the two Detail screens size it differently and the two What's New
 * cards have none at all.
 */

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

import type { GameId } from '../lib/types.ts';
import { VideoComposer } from './VideoComposer.tsx';
import { useQd } from '../store/index.ts';
import { mediaVM } from '../store/selectors.ts';
import type { MediaItem } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

/**
 * Placeholder cells in the loading strip. Six overflows every surface that
 * mounts the carousel (the 640 modal fits ~5 desktop cells, the 402 sheet ~4),
 * which is the point: the strip must read as cut off rather than as a short
 * real set. The row's height comes from `thumbH`, so the number is free.
 */
const SKEL_CELLS = 6;

/** Every control here is a real `<button>`, so each one starts from zero. */
const BARE: CSSProperties = {
  appearance: 'none',
  margin: 0,
  padding: 0,
  border: 0,
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  lineHeight: 1,
};

const STAGE: CSSProperties = {
  position: 'relative',
  width: '100%',
  aspectRatio: '16/9',
  border: '1px solid var(--gt-line)',
  background: '#000',
  overflow: 'hidden',
  boxSizing: 'border-box',
};

/** Fills the stage box; the three kinds differ only in `objectFit`. */
const FILL: CSSProperties = { position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' };

const NAV: CSSProperties = {
  ...BARE,
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--gt-overlay)',
  border: '1px solid var(--gt-line)',
  color: 'var(--gt-text-2)',
  fontFamily: MONO,
  fontSize: 15,
};

/** desktop-only ‹ › at the strip's ends — there is no swipe with a mouse */
const STRIP_NUDGE: CSSProperties = {
  flex: 'none',
  width: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid var(--gt-border-2)',
  // Stated rather than inherited. On a `<button>` this is what the UA stylesheet
  // already says, so it changes nothing there — but the loading strip paints the
  // same box on a `<span>`, which defaults to `content-box` and was 2px taller
  // for it. This repo has no universal `box-sizing` reset to fall back on.
  boxSizing: 'border-box',
  color: 'var(--gt-text-5)',
  fontFamily: MONO,
  fontSize: 13,
};

/**
 * The strip cell's image. Twitch has none: a VOD or clip still needs a Helix
 * call that would put a client id and an app token in the bundle, which is the
 * posture the iframe-only rule exists to protect. Those cells paint
 * `thumbBg` (the game's own gradient) instead — a placeholder, not an answer.
 */
const thumbOf = (it: MediaItem): string =>
  it.kind === 'mov' ? it.poster : it.kind === 'tw' ? '' : it.thumb;

/** The gradient a Twitch cell paints in place of a still. */
const thumbBgOf = (it: MediaItem): string => (it.kind === 'tw' ? it.thumbBg : '');

/** The accent tag marking which service a crew clip came from. */
const srcTagOf = (it: MediaItem): string =>
  it.kind === 'yt' ? 'YT' : it.kind === 'tw' ? 'TW' : '';

/** `▶` on anything that plays; a screenshot gets no glyph. */
const plays = (it: MediaItem): boolean => it.kind !== 'shot';

/** `title` is the member's own words for a crew clip, so it may be empty. */
const labelOf = (it: MediaItem, i: number): string =>
  it.title || (it.kind === 'yt' ? 'PARTY VIDEO ' + (i + 1) : 'MEDIA ' + (i + 1));

type MovItem = Extract<MediaItem, { kind: 'mov' }>;

/** Native HLS — Safari and every iOS browser. `''` on a jsdom element. */
const playsHlsNatively = (el: HTMLVideoElement): boolean =>
  typeof el.canPlayType === 'function' && !!el.canPlayType('application/vnd.apple.mpegurl');

/**
 * Why a trailer cannot be watched HERE, as opposed to at all. Neither of these
 * says anything about the trailer, so neither may reach `prune` — see the
 * component's Failure note.
 */
const BLOCKED_TXT: Record<string, string> = {
  nomse: 'THIS BROWSER CANNOT PLAY STEAM TRAILERS',
  chunk: 'VIDEO PLAYER FAILED TO LOAD',
};

/**
 * One Steam trailer.
 *
 * `hls.js` is imported DYNAMICALLY. Its own chunk is ~157 kB gz — more than the
 * app entry — for a panel that lives below the fold on two screens, so it must
 * not sit on the critical path of a cold Discover load. The import fires
 * when a trailer becomes the ACTIVE tile (only the active one is ever mounted),
 * which means it is downloading while the user looks at the poster rather than
 * after they press play. Safari never pays it at all: it plays HLS natively and
 * that branch returns before the import.
 *
 * Bandwidth: `preload="none"` governs the native path. On the `hls.js` path
 * `loadSource` fetches the MANIFEST as soon as the tile is active — `autoStartLoad`
 * does not gate that, only the fragments after `MANIFEST_PARSED` (hls.mjs:35370) —
 * so paging through the strip costs a poster plus a couple of kB of playlist, and
 * the megabytes still wait for `startLoad()` on the element's own `play` event.
 * `start` is once-only: `startLoad()` calls `stopLoad()` first, so re-firing it on
 * a second `play` after a pause would tear the buffer down and re-buffer.
 *
 * Failure, and the whole point of this component: **only the trailer itself may
 * cost the trailer its tile.** `hls.js` reports plenty of non-fatal errors it
 * handles internally, and its recipe for the fatal ones is one recovery attempt
 * per class before giving up — that, and a native `error` event on Safari, is the
 * only route to `onErr`/`prune`. A browser without Media Source Extensions and a
 * chunk that failed to download are conditions of the ENVIRONMENT: they say the
 * user cannot watch it *here*, not that there is nothing to watch, so they render
 * a caption over the poster and leave the item — and the `N/M` count — alone.
 * Pruning on those was the reported bug wearing a different hat.
 */
function SteamTrailer({ item, onErr }: { item: MovItem; onErr: () => void }): JSX.Element {
  const ref = useRef<HTMLVideoElement | null>(null);
  // The effect must not re-run because the parent re-rendered a new closure.
  const errRef = useRef(onErr);
  errRef.current = onErr;
  const [blocked, setBlocked] = useState('');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (playsHlsNatively(el)) {
      // Safari and iOS. The element is the player, so the element's own `error`
      // is the fatal-stream signal — this is where the `<video onError>` the
      // pre-carousel markup carried has to be re-tied, or a dead trailer would
      // sit there as a black rectangle giving no signal at all.
      const onNativeErr = (): void => errRef.current();
      el.addEventListener('error', onNativeErr);
      el.src = item.hls;
      return () => {
        el.removeEventListener('error', onNativeErr);
        // A detached media element holding a source keeps its network activity
        // alive; React unmounting the node is not enough to stop it.
        el.removeAttribute('src');
        el.load();
      };
    }

    let dead = false;
    let detach: (() => void) | null = null;

    // The FULL build, not `hls.js/light`. Steam muxes nothing: its master
    // playlist declares audio as a separate `EXT-X-MEDIA:TYPE=AUDIO` rendition
    // that the variants reference with `AUDIO="audio"`, and alternate-audio is
    // the first thing the light build drops (its README lists `AudioStreamController`
    // among the types it does not ship). Light would play every trailer SILENTLY,
    // which is a worse bug than the one being fixed and an invisible one in review.
    void import('hls.js')
      .then(({ default: Hls }) => {
        // Unmounted while the chunk was in flight — nothing to attach to, and
        // `dead` is what keeps this from resurrecting a torn-down tile.
        if (dead) return;
        if (!Hls.isSupported()) {
          setBlocked('nomse');
          return;
        }

        const hls = new Hls({ autoStartLoad: false });
        let started = false;
        let recoveredNet = false;
        let recoveredMedia = false;
        const start = (): void => {
          if (started) return;
          started = true;
          hls.startLoad();
        };

        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (!data.fatal) return;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !recoveredNet) {
            recoveredNet = true;
            hls.startLoad();
            return;
          }
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !recoveredMedia) {
            recoveredMedia = true;
            hls.recoverMediaError();
            // `recoverMediaError` only resumes loading when the element has
            // already advanced past 0 (hls.mjs:36149) — at a standing start with
            // `autoStartLoad:false` it swaps the buffer and stops, so the retry
            // has to be asked for or the budget is spent without one.
            hls.startLoad();
            return;
          }
          errRef.current();
        });

        hls.loadSource(item.hls);
        hls.attachMedia(el);
        el.addEventListener('play', start);
        // The user can beat the chunk to the punch: `controls` are live from
        // first paint, so a press during the import fires `play` before the
        // listener exists and would otherwise hang on a player that never
        // started loading.
        if (!el.paused) start();
        detach = () => {
          el.removeEventListener('play', start);
          hls.destroy();
        };
      })
      .catch(() => {
        // The chunk itself failed to load (offline, a stale deploy's hashed
        // filename). Nothing about the TRAILER is wrong, so the tile stays.
        if (!dead) setBlocked('chunk');
      });

    return () => {
      dead = true;
      detach?.();
    };
  }, [item.hls]);

  return (
    <>
      <video
        ref={ref}
        controls
        preload="none"
        poster={item.poster}
        style={{ ...FILL, objectFit: 'contain', background: '#000' }}
      />
      {blocked ? (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            background: 'var(--gt-overlay)',
            borderTop: '1px solid var(--gt-line)',
            color: 'var(--gt-text-5)',
            fontFamily: MONO,
            fontSize: 9.5,
            textAlign: 'center',
            padding: '7px 8px',
            pointerEvents: 'none',
          }}
        >
          {BLOCKED_TXT[blocked]} — SEARCH YOUTUBE BELOW
        </div>
      ) : null}
    </>
  );
}

/**
 * The active tile. `key` is load-bearing on all three: without it React reuses
 * the DOM node between two items of the same kind, which leaves a `<video>`
 * showing the previous poster and navigates the iframe in place.
 */
function stage(it: MediaItem, i: number, onErr: () => void): JSX.Element {
  if (it.kind === 'yt') {
    return (
      <iframe
        key={it.id}
        src={it.embed}
        title={it.title || 'Party video'}
        allowFullScreen
        referrerPolicy="origin"
        style={{ ...FILL, border: 0 }}
      />
    );
  }
  if (it.kind === 'tw') {
    // The prototype shows a poster and OPEN ON TWITCH ↗ because player.twitch.tv
    // needs a `parent` matching the serving domain and a design-tool host is not
    // one. The app has its own domain, so it mounts the real player — and the
    // annotation that said otherwise is prototype-only and deliberately absent.
    return (
      <iframe
        key={it.id}
        src={it.embed}
        title={it.title || 'Party clip'}
        allowFullScreen
        referrerPolicy="origin"
        style={{ ...FILL, border: 0 }}
      />
    );
  }
  if (it.kind === 'mov') return <SteamTrailer key={it.id} item={it} onErr={onErr} />;
  return (
    <img
      key={it.id}
      src={it.full}
      alt={labelOf(it, i)}
      loading="lazy"
      referrerPolicy="no-referrer"
      style={{ ...FILL, objectFit: 'contain' }}
    />
  );
}

export interface MediaCarouselProps {
  gameId: GameId;
  /** desktop tree: larger thumbs plus the ‹ › strip nudges (no touch swipe) */
  desktop?: boolean;
  /**
   * Hide the footer row — `SEARCH YOUTUBE ↗` and the `+ CUSTOM VIDEOS`
   * composer.
   *
   * Both What's New cards pass it. That card is a look-at-the-game-and-make-a-
   * call surface, so video curation does not belong on it; it stays on the two
   * Game Detail screens, which are unchanged.
   *
   * This does cost the empty state its way out — P10-01 recorded
   * `SEARCH YOUTUBE ↗` as both the footer link *and* the escape hatch when a
   * game has no media at all. Upstream accepts that consequence on this surface
   * (`Media Carousel.dc.html` gates the whole row on `hasFoot`, and its empty
   * state sits above the gate), and it is the right trade here: a What's New
   * card with no media still has its whole reason for existing — the S/A/B/C
   * strip — sitting directly underneath.
   */
  noFoot?: boolean;
}

/**
 * The active index, the pruned set and the composer are all per-game, so the
 * game id is a remount key rather than four reset effects that can each forget
 * a field.
 */
export function MediaCarousel({ gameId, desktop, noFoot }: MediaCarouselProps): JSX.Element {
  return (
    <Carousel key={String(gameId)} gameId={gameId} desktop={!!desktop} noFoot={!!noFoot} />
  );
}

function Carousel({
  gameId,
  desktop,
  noFoot,
}: {
  gameId: GameId;
  desktop: boolean;
  noFoot: boolean;
}): JSX.Element {
  const s = useQd();
  const vm = mediaVM(s, gameId);
  const g = s.gById(gameId);
  const appid = g ? g.appid : 0;
  const ensureMedia = s.ensureMedia;

  const [idx, setIdx] = useState(0);
  const [pruned, setPruned] = useState<string[]>([]);
  // The composer owns its own fields now that it is an overlay — this is only
  // whether it is up.
  const [open, setOpen] = useState(false);
  const stripRef = useRef<HTMLDivElement | null>(null);

  // The Detail path already has trailers + screenshots (`openGame` → `enrichGame`);
  // What's New never runs that, and this covers both. Single-flighted and a
  // no-op once `media[appid]` is populated, so calling it here is free.
  useEffect(() => {
    if (appid > 0) ensureMedia(appid);
  }, [appid, ensureMedia]);

  const items = pruned.length ? vm.items.filter((it) => !pruned.includes(it.id)) : vm.items;
  const count = items.length;
  const at = count ? Math.min(idx, count - 1) : 0;
  const active = items[at];

  /**
   * Is the Steam half still in the air?
   *
   * `applyEnrich` writes `media[appid]` exactly once per appid, so PRESENCE is
   * the answer-has-landed signal and absence means the round trip `ensureMedia`
   * fired above has not come back. A row with no appid is never pending —
   * nothing is coming for it — which is what keeps a curated non-Steam game out
   * of the reserved layout below.
   *
   * This exists to HOLD THE BOX STILL. Under the stage the carousel is two rows
   * that only exist once there is something for them to say (a title; a second
   * item), so the same carousel is shorter before the payload lands than after —
   * 102px in the 640 modal, 86px in the 402 sheet. And it is BAND 1 of the
   * What's New modal, whose height is its content and which is centred inside
   * its own overlay, so the growth moved the modal's top edge up 51px with it:
   * every control below the carousel jumped while the reader was reaching for
   * one. Both rows are therefore reserved while `pending`.
   *
   * What this does NOT promise: a game whose payload comes back with nothing
   * still collapses when the skeleton clears. Reserving forever would mean an
   * empty thumb strip under every media-less game, which is a permanent cost
   * for a case an appid'd row almost never hits (Steam's `appdetails` carries
   * screenshots even when it carries no trailer).
   */
  const pending = appid > 0 && !s.media[appid];

  const thumbH = desktop ? 64 : 50;
  const thumbW = Math.round((thumbH * 16) / 9);
  const navW = desktop ? 34 : 32;
  const footFs = desktop ? 10 : 9.5;

  /**
   * Scrolling happens HERE and not in an effect on the active index: an effect
   * would also fire when a Steam payload lands, and `scrollIntoView` on a
   * carousel that is still below the fold would yank the page down to it.
   */
  const select = (i: number): void => {
    if (!count) return;
    const n = ((i % count) + count) % count;
    setIdx(n);
    const el = stripRef.current?.querySelector('[data-mi="' + n + '"]');
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  /** An unrecoverable trailer leaves the set; the clamp above lands on its successor. */
  const prune = (id: string): void => {
    const i = items.findIndex((x) => x.id === id);
    setPruned((p) => (p.includes(id) ? p : [...p, id]));
    // Something BEFORE the active tile vanishing must not change what is on stage.
    if (i >= 0 && i < at) setIdx((n) => Math.max(0, n - 1));
  };

  const nudge = (dx: number): void => {
    const strip = stripRef.current;
    // Instant, not smooth: every scroll in this app is a hard cut (the drag
    // engines' autoscroll, `select`'s own scrollIntoView), and the aesthetic is
    // hard edges throughout — one animated scroller would stand alone.
    if (strip && typeof strip.scrollBy === 'function') strip.scrollBy({ left: dx, behavior: 'auto' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: desktop ? 9 : 8 }}>
      {/* --- stage ---------------------------------------------------------- */}
      {active ? (
        <div style={STAGE}>
          {stage(active, at, () => prune(active.id))}
          {count > 1 ? (
            <>
              <button
                type="button"
                aria-label="PREVIOUS MEDIA"
                onClick={() => select(at - 1)}
                style={{ ...NAV, left: 0, width: navW, height: navW + 10 }}
              >
                ‹
              </button>
              <button
                type="button"
                aria-label="NEXT MEDIA"
                onClick={() => select(at + 1)}
                style={{ ...NAV, right: 0, width: navW, height: navW + 10 }}
              >
                ›
              </button>
              <div
                style={{
                  position: 'absolute',
                  right: 6,
                  // clear of a `<video>`'s own control bar, which owns that corner
                  bottom: active.kind === 'mov' ? 46 : 6,
                  background: 'var(--gt-overlay)',
                  border: '1px solid var(--gt-line)',
                  color: 'var(--gt-text-5)',
                  fontFamily: MONO,
                  fontSize: 9,
                  padding: '3px 6px',
                  pointerEvents: 'none',
                }}
              >
                {at + 1}/{count}
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <div style={{ ...STAGE, background: 'var(--gt-checker)' }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: PIXEL,
              fontSize: desktop ? 8 : 7,
              color: 'var(--gt-muted)',
            }}
          >
            {/* `NO MEDIA YET` is a VERDICT, and it is not one we have until the
                payload lands — the stage used to assert it for the whole round
                trip and then replace it with a trailer. `LOADING…` is the same
                word the chart's pending state uses (`detailVM.noChartWhy`). */}
            {pending ? 'LOADING…' : 'NO MEDIA YET'}
          </div>
        </div>
      )}

      {/* --- title only ------------------------------------------------------
          THE ATTRIBUTION AND THE REMOVE BOTH MOVED INTO THE COMPOSER (W3).
          This row used to read `<title> · ADDED BY <NAME> · <AGO>` with the
          author's own ✕ beside it, always visible. The design replaces it with
          the composer's list, so both now cost a click — real information loss
          from an always-visible surface, taken deliberately (Marcus,
          2026-08-13) rather than as a port oversight.

          The title survives because it names what is ON STAGE, which is a
          property of the thing being looked at rather than of who supplied it.

          THE ROW IS UNCONDITIONAL — `minHeight:20` was already reserving space
          for a title that wrapped, but the row itself used to come and go, so
          the 20px moved everything under it: on arrival when the payload landed,
          and again when paging from a Steam screenshot (always titled) to a crew
          clip whose author left the field empty (`labelOf`'s fallback is for the
          strip's aria-label, not for this line). An empty row is 20px of quiet;
          a row that appears and disappears is the modal moving. */}
      <div
        style={{
          minHeight: 20,
          fontFamily: MONO,
          fontSize: desktop ? 10 : 9.5,
          color: 'var(--gt-muted-2)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {active ? active.title : ''}
      </div>

      {/* --- thumb strip ---------------------------------------------------- */}
      {count > 1 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {desktop ? (
            <button
              type="button"
              className="d-hov-bright"
              aria-label="SCROLL THUMBNAILS LEFT"
              onClick={() => nudge(-200)}
              style={{ ...BARE, ...STRIP_NUDGE, height: thumbH }}
            >
              ‹
            </button>
          ) : null}
          {/* PORTING-NOTES P10-02: the desktop tree's first horizontal scroller. */}
          <div
            ref={stripRef}
            data-hs="1"
            style={{ display: 'flex', gap: 6, overflowX: 'auto', flex: 1, minWidth: 0 }}
          >
            {items.map((it, i) => (
              <button
                key={it.id}
                type="button"
                data-mi={i}
                aria-label={'SHOW ' + labelOf(it, i)}
                aria-current={i === at}
                onClick={() => select(i)}
                style={{
                  ...BARE,
                  position: 'relative',
                  flex: 'none',
                  width: thumbW,
                  height: thumbH,
                  background: '#000',
                  border: i === at ? '1px solid var(--acc)' : '1px solid var(--gt-border)',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  opacity: i === at ? 1 : 0.72,
                }}
              >
                {thumbOf(it) ? (
                  <img
                    src={thumbOf(it)}
                    alt=""
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <span
                    style={{ display: 'block', width: '100%', height: '100%', background: thumbBgOf(it) }}
                  />
                )}
                {srcTagOf(it) ? (
                  <span
                    style={{
                      position: 'absolute',
                      left: 3,
                      top: 2,
                      fontFamily: MONO,
                      fontSize: 8,
                      lineHeight: 1,
                      color: 'var(--gt-ink)',
                      background: 'var(--acc)',
                      padding: '2px 3px',
                    }}
                  >
                    {srcTagOf(it)}
                  </span>
                ) : null}
                {plays(it) ? (
                  <span
                    style={{
                      position: 'absolute',
                      right: 3,
                      bottom: 2,
                      fontFamily: MONO,
                      fontSize: 9,
                      lineHeight: 1,
                      color: 'var(--gt-text)',
                      background: 'var(--gt-overlay)',
                      padding: '2px 3px',
                    }}
                  >
                    ▶
                  </span>
                ) : null}
              </button>
            ))}
          </div>
          {desktop ? (
            <button
              type="button"
              className="d-hov-bright"
              aria-label="SCROLL THUMBNAILS RIGHT"
              onClick={() => nudge(200)}
              style={{ ...BARE, ...STRIP_NUDGE, height: thumbH }}
            >
              ›
            </button>
          ) : null}
        </div>
      ) : pending ? (
        /* The strip's placeholder. Same outer row, same cell box, same nudges,
           so the swap to the real strip changes what the cells CONTAIN and
           nothing about where anything sits. `SKEL_CELLS` overflows the widest
           surface on purpose — the count is arbitrary because the row's height
           is `thumbH` either way, and a short skeleton under a wide modal reads
           as "two screenshots" rather than as "still loading".

           Inert on purpose: no `<button>`, and `aria-hidden` so a screen reader
           is told about the stage's `LOADING…` and not about six empty cells. */
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} aria-hidden>
          {desktop ? <span style={{ ...STRIP_NUDGE, height: thumbH }}>‹</span> : null}
          <div style={{ display: 'flex', gap: 6, overflow: 'hidden', flex: 1, minWidth: 0 }}>
            {Array.from({ length: SKEL_CELLS }, (_, i) => (
              <span
                key={i}
                style={{
                  flex: 'none',
                  width: thumbW,
                  height: thumbH,
                  background: 'var(--gt-checker)',
                  border: '1px solid var(--gt-border)',
                  boxSizing: 'border-box',
                  opacity: 0.72,
                }}
              />
            ))}
          </div>
          {desktop ? <span style={{ ...STRIP_NUDGE, height: thumbH }}>›</span> : null}
        </div>
      ) : null}

      {/* --- footer: the escape hatch + the composer ------------------------
          Gated as one unit, matching `Media Carousel.dc.html`'s single
          `sc-if hasFoot` — the composer form below is inside the same gate,
          because a `noFoot` mount must not be able to leave an open composer
          on screen with no control that opened it. */}
      {noFoot ? null : (
      <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
          fontFamily: MONO,
          fontSize: footFs,
          color: 'var(--gt-muted-2)',
        }}
      >
        <a href={vm.ytLink} target="_blank" rel="noreferrer">
          SEARCH YOUTUBE ↗
        </a>
        {vm.canAdd ? (
          <button
            type="button"
            aria-label="ADD A YOUTUBE OR TWITCH VIDEO"
            onClick={() => setOpen(true)}
            style={{
              ...BARE,
              fontFamily: MONO,
              fontSize: footFs,
              color: 'var(--acc)',
              border: '1px solid var(--gt-border-2)',
              padding: '6px 8px',
            }}
          >
            + CUSTOM VIDEOS
          </button>
        ) : vm.addTip ? (
          <span>{vm.addTip}</span>
        ) : null}
      </div>

      </>
      )}

      {/* The composer is an OVERLAY now, and deliberately outside the `noFoot`
          gate above: `noFoot` hides the trigger, and with no trigger the flag
          cannot be true — where the old inline form had to be inside the gate
          because it rendered in the flow beneath it. */}
      {vm.canAdd && open ? (
        <VideoComposer gameId={gameId} desktop={desktop} onClose={() => setOpen(false)} />
      ) : null}
    </div>
  );
}
